/**
 * Strategic Assessment View
 * 
 * Comprehensive country/region strategic analysis.
 */

import React, { useState } from 'react';
import { Globe, Search, Loader2, Shield, Swords, DollarSign, Users } from 'lucide-react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

export default function StrategicAssessment({ apiUrl }) {
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [bilateralMode, setBilateralMode] = useState(false);
  const [countryB, setCountryB] = useState('');

  const countries = [
    'USA', 'China', 'Russia', 'Japan', 'India', 'UK', 'France', 'Germany',
    'South Korea', 'Australia', 'Taiwan', 'Iran', 'Israel', 'Saudi Arabia',
    'Turkey', 'Brazil', 'Indonesia', 'Pakistan', 'Vietnam'
  ];

  async function runAssessment() {
    if (!country) return;
    
    setLoading(true);
    
    try {
      const endpoint = bilateralMode && countryB 
        ? '/assess/bilateral' 
        : '/assess/country';
      
      const body = bilateralMode && countryB
        ? { countryA: country, countryB }
        : { country };

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      setAssessment(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const getCapabilityData = () => {
    if (!assessment?.countryProfile?.military?.capabilityScores) return [];
    const scores = assessment.countryProfile.military.capabilityScores;
    return [
      { subject: 'Conventional', value: (scores.conventional || 0) * 100 },
      { subject: 'Nuclear', value: (scores.nuclear || 0) * 100 },
      { subject: 'Naval', value: (scores.naval || 0) * 100 },
      { subject: 'Air Power', value: (scores.airPower || 0) * 100 },
      { subject: 'Space/Cyber', value: (scores.spaceAndCyber || 0) * 100 },
      { subject: 'Projection', value: (scores.powerProjection || 0) * 100 },
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Strategic Assessment</h1>
        <p className="text-slate-400">Comprehensive analysis of national capabilities and relationships</p>
      </div>

      {/* Mode Toggle */}
      <div className="flex space-x-2">
        <button
          onClick={() => setBilateralMode(false)}
          className={`px-4 py-2 rounded-lg ${!bilateralMode ? 'bg-cyan-600' : 'bg-slate-700'}`}
        >
          Single Country
        </button>
        <button
          onClick={() => setBilateralMode(true)}
          className={`px-4 py-2 rounded-lg ${bilateralMode ? 'bg-cyan-600' : 'bg-slate-700'}`}
        >
          Bilateral Analysis
        </button>
      </div>

      {/* Input */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-slate-400 mb-2">
              {bilateralMode ? 'Country A' : 'Country'}
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3"
            >
              <option value="">Select country...</option>
              {countries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          
          {bilateralMode && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-slate-400 mb-2">Country B</label>
              <select
                value={countryB}
                onChange={(e) => setCountryB(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3"
              >
                <option value="">Select country...</option>
                {countries.filter(c => c !== country).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end">
            <button
              onClick={runAssessment}
              disabled={loading || !country}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium
                       disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Globe className="w-5 h-5 mr-2" />
                  Assess
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {assessment && !assessment.error && (
        <div className="space-y-6">
          {/* Country Profile Header */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-bold mb-4">
              {assessment.target || assessment.countries?.join(' ↔ ')}
            </h2>
            
            {/* Strategic Position */}
            {assessment.countryProfile?.strategicPosition && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="text-sm text-slate-400">Power Tier</div>
                  <div className="text-xl font-bold text-cyan-400 capitalize">
                    {assessment.countryProfile.strategicPosition.tier?.replace('_', ' ')}
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="text-sm text-slate-400">Overall Power</div>
                  <div className="text-xl font-bold text-cyan-400">
                    {((assessment.countryProfile.strategicPosition.overallPower || 0) * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="text-sm text-slate-400">Trajectory</div>
                  <div className="text-xl font-bold text-cyan-400 capitalize">
                    {assessment.countryProfile.strategicPosition.trajectory}
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="text-sm text-slate-400">Global Rank</div>
                  <div className="text-xl font-bold text-cyan-400">
                    #{assessment.countryProfile?.military?.ranking?.rank || 'N/A'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Capability Radar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Military Capabilities</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={getCapabilityData()}>
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="subject" stroke="#94a3b8" />
                  <PolarRadiusAxis domain={[0, 100]} stroke="#334155" />
                  <Radar
                    name="Capability"
                    dataKey="value"
                    stroke="#06b6d4"
                    fill="#06b6d4"
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Strengths & Weaknesses */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Strengths & Weaknesses</h3>
              
              {assessment.countryProfile?.military?.strengths && (
                <div className="mb-4">
                  <div className="text-sm text-slate-400 mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-1 text-green-500" />
                    Strengths
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {assessment.countryProfile.military.strengths.map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {assessment.countryProfile?.military?.weaknesses && (
                <div>
                  <div className="text-sm text-slate-400 mb-2 flex items-center">
                    <Swords className="w-4 h-4 mr-1 text-red-500" />
                    Weaknesses
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {assessment.countryProfile.military.weaknesses.map((w, i) => (
                      <span key={i} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
                        {w}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bilateral Specific */}
          {bilateralMode && assessment.militaryBalance && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Military Balance</h3>
              <div className="text-center text-xl font-bold text-cyan-400">
                {assessment.militaryBalance.overallBalance?.leader || 'Near Parity'}
              </div>
            </div>
          )}

          {/* Raw Data */}
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-4 cursor-pointer hover:bg-slate-700/50">
              View Raw Assessment Data
            </summary>
            <pre className="p-4 text-xs overflow-auto max-h-96 bg-slate-900">
              {JSON.stringify(assessment, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
